import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../hooks/useToast'
import type { DeveloperResponse, UserSessionResponse } from '../../interfaces'
import PasswordInput from '../PasswordInput'
import SetPasswordAlert from '../SetPasswordAlert'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  selectedKeyId: string | null
  request: Req<DeveloperResponse>
  onClose(): void
  setSelectedKeyId(string?): void
  getData(): Promise<any>
}

interface FormProps {
  email: string
  password: string
}

const DeleteApiKeyModal = ({
  isOpen,
  onClose,
  user,
  selectedKeyId,
  setSelectedKeyId,
  getData,
  request,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')
  const toast = useToast()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ email: user?.userEmail ?? '', password: '' }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              await request.delete(
                `/api/clients/${user.clientNumber}/settings/developer/keys/${selectedKeyId}`,
                {
                  userEmail: values.email,
                  userPassword: values.password,
                },
              )
              if (request.error) {
                setFormError(
                  t(
                    'invalid_account_credentials_entered',
                    'You have entered invalid account credentials.',
                  ),
                )
              } else {
                toast({
                  description: t(
                    'successfully_deleted_api_key',
                    'Your API keys have been successfully deleted',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                onClose()
                setSelectedKeyId(null)
                await getData()
              }
            }
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string().required('Required'),
            password: Yup.string().required('Required'),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>
                {t('delete_api_keys', 'Delete API keys')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {!user?.isPasswordSetByUser ? <SetPasswordAlert /> : null}
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <VStack spacing={4}>
                  <ChakraText>
                    {t(
                      'delete_api_keys_description',
                      "After it is deleted, you'll be unable to perform any actions with the keys.",
                    )}
                  </ChakraText>
                  <Field name="email">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.email && form.touched.email,
                        )}
                        isReadOnly
                        isRequired
                        isDisabled
                      >
                        <FormLabel>{t('email', 'Email')}</FormLabel>
                        <Input {...field} placeholder={t('email', 'Email')} />
                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="password">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.password && form.touched.password,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('password', 'Password')}</FormLabel>
                        <PasswordInput
                          field={field}
                          isDisabled={!user?.isPasswordSetByUser}
                          isPasswordSetByUser={
                            user?.isPasswordSetByUser ?? false
                          }
                          showToggle={true}
                        />
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={!isValid || !dirty}
                  colorScheme="appRed"
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default DeleteApiKeyModal
